import { t } from "i18next";

import { LabelDecal, LabelKey } from "./labels";

const LABEL_TRANSLATION_KEYS: Record<LabelKey, string> = {
    top: "axisSphere.top",
    bottom: "axisSphere.bottom",
    left: "axisSphere.left",
    right: "axisSphere.right",
    front: "axisSphere.front",
    back: "axisSphere.back",
    north: "axisSphere.north",
    south: "axisSphere.south",
    east: "axisSphere.east",
    west: "axisSphere.west",
};

const FONT_FAMILY = `"Open Sans", sans-serif`;
/** Arbitrary size used only to measure the font's cap height before picking the real size. */
const MEASURE_FONT_SIZE = 100;
/**
 * Texel height of every label texture. The sphere renders at a fixed on-screen size where a label
 * needs roughly 16 device pixels of cap height, so this leaves ample headroom for high-DPI displays
 * and keeps the textures independent of `devicePixelRatio`.
 */
const TEXTURE_HEIGHT = 64;
const MAX_TEXTURE_WIDTH = 512;

/**
 * Cap height as a fraction of the decal box height. Mirrors `LABEL_HEIGHT_HEADROOM` in `labels.ts`:
 * the box is twice the glyph height, so the glyph fills the middle half of the texture.
 */
const CAP_HEIGHT_FRACTION = 0.5;

export function labelText(labelKey: LabelKey): string {
    return t(LABEL_TRANSLATION_KEYS[labelKey]);
}

function labelKeys(): LabelKey[] {
    return Object.keys(LABEL_TRANSLATION_KEYS) as LabelKey[];
}

/** How long to wait for the webfont before rasterizing with whatever the browser substitutes. */
const FONT_LOAD_TIMEOUT = 3000;

/**
 * Give the label font a chance to arrive before the first rasterization. Best effort only: the wait
 * is capped, and `document.fonts.load` resolves immediately while the font's stylesheet has yet to
 * register any faces. Either way the fallback gets baked in, so pair this with
 * `onFontLoadingDone` to redraw the labels once the real font turns up.
 */
export async function loadLabelFont(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            document.fonts?.load(`${TEXTURE_HEIGHT}px ${FONT_FAMILY}`),
            new Promise((resolve) => {
                timer = setTimeout(resolve, FONT_LOAD_TIMEOUT);
            }),
        ]);
    } catch {
        // Fall through to whatever the browser substitutes.
    } finally {
        clearTimeout(timer);
    }
}

/** Run `callback` whenever a batch of font loading finishes. Returns an unsubscribe function. */
export function onFontLoadingDone(callback: () => void): () => void {
    const fonts = document.fonts;
    if (!fonts) {
        return () => {};
    }
    fonts.addEventListener("loadingdone", callback);
    return () => fonts.removeEventListener("loadingdone", callback);
}

/**
 * Width of every label laid end to end, used to tell whether a font batch actually changed the
 * glyphs the sphere draws. `document.fonts.check` can't answer that: it reports true while the
 * stylesheet has yet to register any faces, and passing the full family string makes it true
 * regardless, since the generic fallback is always available. Measuring sidesteps the question —
 * the number only moves when the rendered text does.
 */
export function labelFontFingerprint(): number {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) {
        return 0;
    }
    ctx.font = `${MEASURE_FONT_SIZE}px ${FONT_FAMILY}`;
    return ctx.measureText(labelKeys().map(labelText).join("")).width;
}

/** Draw a single label centered in a cell matching the decal box's aspect ratio. */
function rasterizeLabel(text: string, boxAspect: number): { width: number; height: number; alpha: Uint8Array } | null {
    const height = TEXTURE_HEIGHT;
    const width = Math.max(1, Math.min(MAX_TEXTURE_WIDTH, Math.round(height * boxAspect)));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
        return null;
    }

    // Size from a script-independent cap height so every label renders at the same visual size,
    // rather than each string being stretched to fill its own box.
    ctx.font = `${MEASURE_FONT_SIZE}px ${FONT_FAMILY}`;
    const capHeight = ctx.measureText("H").actualBoundingBoxAscent || MEASURE_FONT_SIZE * 0.7;
    let fontSize = (MEASURE_FONT_SIZE * (height * CAP_HEIGHT_FRACTION)) / capHeight;

    ctx.font = `${fontSize}px ${FONT_FAMILY}`;
    const maxWidth = width * 0.98;
    const textWidth = ctx.measureText(text).width;
    if (textWidth > maxWidth) {
        fontSize *= maxWidth / textWidth;
        ctx.font = `${fontSize}px ${FONT_FAMILY}`;
    }

    // Center the glyphs' actual ink box rather than the em box, so all-caps Latin, CJK and Thai all
    // end up visually centered on the face.
    const metrics = ctx.measureText(text);
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(
        text,
        width / 2,
        height / 2 + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2,
    );

    // Keep only coverage; the shader tints it, so hover/active states need no re-rasterization.
    // Rows are flipped because texture V runs bottom-up while canvas rows run top-down, and
    // UNPACK_FLIP_Y_WEBGL only applies to canvas/image sources, not raw arrays.
    const rgba = ctx.getImageData(0, 0, width, height).data;
    const alpha = new Uint8Array(width * height);
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            alpha[(height - 1 - y) * width + x] = rgba[(y * width + x) * 4 + 3];
        }
    }

    return { width, height, alpha };
}

/** Fully transparent stand-in for meshes that carry no label. */
export function createEmptyLabelTexture(gl: WebGL2RenderingContext): WebGLTexture {
    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 1, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array([0]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
}

/**
 * Rasterize one texture per label. Separate textures rather than an atlas so mipmapped minification
 * at glancing view angles can't bleed neighbouring labels into each other.
 */
export function createLabelTextures(
    gl: WebGL2RenderingContext,
    decals: ReadonlyMap<string, LabelDecal>,
): Map<LabelKey, WebGLTexture> {
    const aspects = new Map<LabelKey, number>();
    for (const decal of decals.values()) {
        if (!aspects.has(decal.labelKey)) {
            aspects.set(decal.labelKey, decal.halfSize[0] / decal.halfSize[1]);
        }
    }

    const anisotropic = gl.getExtension("EXT_texture_filter_anisotropic");
    const textures = new Map<LabelKey, WebGLTexture>();

    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    for (const [labelKey, aspect] of aspects) {
        const raster = rasterizeLabel(labelText(labelKey), aspect);
        if (!raster) {
            continue;
        }

        const texture = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, raster.width, raster.height, 0, gl.RED, gl.UNSIGNED_BYTE, raster.alpha);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.generateMipmap(gl.TEXTURE_2D);
        if (anisotropic) {
            const max = gl.getParameter(anisotropic.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
            gl.texParameterf(gl.TEXTURE_2D, anisotropic.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(4, max));
        }
        textures.set(labelKey, texture);
    }
    gl.bindTexture(gl.TEXTURE_2D, null);

    return textures;
}
